import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
    UserPlus,
    LogOut,
    MonitorDot,
    Database,
    Users,
    Settings,
    ClipboardCheck,
    UserCog,
    BarChart3,
    TrendingUp,
    PieChart,
    FileText,
    Calendar,
    CheckCircle2
} from 'lucide-react';
import { useAuth } from '../AuthContext/AuthContext';
import './Dashboard.css';
import { useTranslation } from 'react-i18next';

// Reusable DashboardCard component for consistency
const DashboardCard = ({ title, description, icon: Icon, route, color, onClick }) => (
    <Card
        className="card-3d-effect h-100"
        onClick={onClick}
        style={{ cursor: 'pointer' }}
    >
        <Card.Body className="d-flex flex-column">
            <div
                className="mb-4 p-3 bg-light rounded-circle d-inline-flex align-items-center justify-content-center"
                style={{ width: '60px', height: '60px' }}
            >
                <Icon size={24} className={`text-${color}`} />
            </div>
            <Card.Title as="h3" className="mb-3">{title}</Card.Title>
            <Card.Text className="text-muted flex-grow-1">
                {description}
            </Card.Text>
            <div className="mt-auto pt-3 border-top">
                <span className={`text-${color} d-flex align-items-center`}>
                    {title && `${title}`}
                    <span className="ms-2">→</span>
                </span>
            </div>
        </Card.Body>
    </Card>
);

const Dashboard = () => {
    const navigate = useNavigate();
    const { isLoggedIn, userRole } = useAuth();
    const { t } = useTranslation('dashboard');

    // Common cards (translated)
    const commonCards = [
        {
            title: t('common_cards.checkin.title'),
            description: t('common_cards.checkin.description'),
            icon: UserPlus,
            route: '/staff-checkin',
            color: 'success'
        },
        {
            title: t('common_cards.checkout.title'),
            description: t('common_cards.checkout.description'),
            icon: LogOut,
            route: '/staff-checkout',
            color: 'primary'
        }
    ];

    // Role-specific cards (translated) - Fixed routes
    const roleSpecificCards = {
        ICT: [
            {
                title: t('role_cards.ICT.ict_dashboard.title'),
                description: t('role_cards.ICT.ict_dashboard.description'),
                icon: MonitorDot,
                route: '/ict-checkout-list',
                color: 'info'
            },
            {
                title: t('role_cards.ICT.asset_management.title'),
                description: t('role_cards.ICT.asset_management.description'),
                icon: Database,
                route: '/ict-asset',
                color: 'warning'
            },
            {
                title: t('role_cards.ICT.supervisor_management.title') || t('supervisorManagement'),
                description: t('role_cards.ICT.supervisor_management.description') || t('manageSupervisorsForStaffAssignments'),
                icon: UserCog,
                route: '/supervisor-management',
                color: 'secondary'
            },
            {
                title: t('role_cards.ICT.duty_station_management.title') || t('dutyStationManagement'),
                description: t('role_cards.ICT.duty_station_management.description') || t('manageDutyStationsForStaffAssignments'),
                icon: UserCog,
                route: '/duty-station-management',
                color: 'secondary'
            },
            {
                title: t('role_cards.ICT.role_management.title'),
                description: t('role_cards.ICT.role_management.description'),
                icon: Settings,
                route: '/role-management',
                color: 'danger'
            },
            {
                title: t('role_cards.ICT.user_admin.title'),
                description: t('role_cards.ICT.user_admin.description'),
                icon: Settings,
                route: '/users',
                color: 'info'
            }
        ],
        SECURITY: [
            {
                title: t('role_cards.SECURITY.security_dashboard.title'),
                description: t('role_cards.SECURITY.security_dashboard.description'),
                icon: ClipboardCheck,
                route: '/security-checkout-list',
                color: 'info'
            },
            {
                title: t('role_cards.SECURITY.staff_list.title'),
                description: t('role_cards.SECURITY.staff_list.description'),
                icon: Users,
                route: '/staff-list',
                color: 'warning'
            },
            {
                title: t('role_cards.ICT.asset_management.title'),
                description: t('role_cards.ICT.asset_management.description'),
                icon: Database,
                route: '/ict-asset',
                color: 'warning'
            }
        ],
        HR: [
            {
                title: t('role_cards.HR.hr_checkout.title'),
                description: t('role_cards.HR.hr_checkout.description'),
                icon: ClipboardCheck,
                route: '/hr-checkout-list',
                color: 'info'
            },
            {
                title: t('role_cards.HR.staff_list.title'),
                description: t('role_cards.HR.staff_list.description'),
                icon: Users,
                route: '/staff-list',
                color: 'warning'
            },
            {
                title: t('staffAnalytics'),
                description: t('detailedStaffMetricsAndOrganizationalInsightsWithDemographicAnalysis'),
                icon: TrendingUp,
                route: '/staff-analytics',
                color: 'primary'
            },
            {
                title: t('trainingAnalytics'),
                description: t('trainingProgramEffectivenessAndCompletionTrackingWithDetailedReports'),
                icon: PieChart,
                route: '/training-analytics',
                color: 'success'
            },
            {
                title: t('role_cards.HR.supervisor_management.title') || t('supervisorManagement'),
                description: t('role_cards.HR.supervisor_management.description') || t('manageSupervisorsForStaffAssignments'),
                icon: UserCog,
                route: '/supervisor-management',
                color: 'secondary'
            },
            {
                title: t('role_cards.HR.duty_station_management.title') || t('dutyStationManagement'),
                description: t('role_cards.HR.duty_station_management.description') || t('manageDutyStationsForStaffAssignments'),
                icon: UserCog,
                route: '/duty-station-management',
                color: 'secondary'
            },
            {
                title: t('completedCheckouts'),
                description: t('viewCompleteCheckoutHistoryWithDatesStatisticsAndDetailedTracking'),
                icon: CheckCircle2,
                route: '/completed-checkouts',
                color: 'success'
            }
        ],
        MANAGEMENT: [
            {
                title: t('executiveReports'),
                description: t('comprehensiveExecutiveReportsWithKpisTrendsAndPerformanceMetrics'),
                icon: FileText,
                route: '/executive-reports',
                color: 'primary'
            },
            {
                title: t('managementAnalytics'),
                description: t('organizationalInsightsWithStaffAssetsAndOperationalAnalytics'),
                icon: BarChart3,
                route: '/management-analytics',
                color: 'info'
            },
            {
                title: t('trainingAnalytics'),
                description: t('trainingProgramEffectivenessAndCompletionTrackingWithDetailedReports'),
                icon: PieChart,
                route: '/training-analytics',
                color: 'success'
            },
            {
                title: t('staffDirectory'),
                description: t('completeStaffDirectoryWithDetailedProfilesAndOrganizationalStructure'),
                icon: Users,
                route: '/staff-list',
                color: 'success'
            },
            {
                title: t('completedCheckouts'),
                description: t('viewCompleteCheckoutHistoryWithDatesStatisticsAndDetailedTracking'),
                icon: CheckCircle2,
                route: '/completed-checkouts',
                color: 'success'
            }
        ]
    };

    // Helper function: Get cards based on user role
    const getCardsForRole = () => {
        if (!userRole || userRole === t('staff')) {
            return commonCards;
        }
        return [...commonCards, ...(roleSpecificCards[userRole] || [])];
    };

    // If user is logged in and role is STAFF
    if (isLoggedIn && userRole === t('staff')) {
        return (
            <Container className="py-5">
                <Row className="mb-4">
                    <Col>
                        <h1 className="display-4 mb-2">{t('title')}</h1>
                        <p className="text-muted">{t('subtitle.staff')}</p>
                    </Col>
                </Row>
                <Row>
                    {commonCards.map((card, index) => (
                        <Col md={6} className="mb-4" key={index}>
                            <DashboardCard
                                {...card}
                                onClick={() => navigate(card.route)}
                            />
                        </Col>
                    ))}
                </Row>
            </Container>
        );
    }

    // For ALL other roles (ICT, HR, SECURITY, MANAGEMENT) - Show dashboard cards
    return (
        <Container className="py-5">
            <Row className="mb-4">
                <Col>
                    <h1 className="display-4 mb-2">{t('title')}</h1>
                    <p className="text-muted">
                        {userRole === t('management')
                            ? t('executiveDashboardWithComprehensiveOrganizationalToolsAndAnalytics')
                            : t('subtitle.default')
                        }
                    </p>
                </Col>
            </Row>
            <Row>
                {getCardsForRole().map((card, index) => (
                    <Col md={6} lg={4} className="mb-4" key={index}>
                        <DashboardCard
                            {...card}
                            onClick={() => navigate(card.route)}
                        />
                    </Col>
                ))}
            </Row>

            {/* Additional Management-specific quick stats */}
            {userRole === t('management') && (
                <Row className="mt-5">
                    <Col>
                        <Card className="border-0 shadow-sm">
                            <Card.Header className="bg-light">
                                <h5 className="mb-0">
                                    <BarChart3 className="me-2" size={20} />
                                    Quick Organization Overview
                                </h5>
                            </Card.Header>
                            <Card.Body>
                                <Row className="text-center">
                                    <Col md={3}>
                                        <div className="mb-2 p-3 border-end">
                                            <h4 className="text-primary mb-1">245</h4>
                                            <small className="text-muted">{t('totalStaff')}</small>
                                            <div className="mt-1">
                                                <small className="text-success">
                                                    <TrendingUp size={12} className="me-1" />
                                                    +5.2%
                                                </small>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="mb-2 p-3 border-end">
                                            <h4 className="text-success mb-1">92%</h4>
                                            <small className="text-muted">{t('assetUtilization')}</small>
                                            <div className="mt-1">
                                                <small className="text-success">
                                                    <TrendingUp size={12} className="me-1" />
                                                    +2.1%
                                                </small>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="mb-2 p-3 border-end">
                                            <h4 className="text-info mb-1">89%</h4>
                                            <small className="text-muted">{t('checkoutEfficiency')}</small>
                                            <div className="mt-1">
                                                <small className="text-danger">
                                                    <TrendingUp size={12} className="me-1" style={{ transform: 'rotate(180deg)' }} />
                                                    -1.2%
                                                </small>
                                            </div>
                                        </div>
                                    </Col>
                                    <Col md={3}>
                                        <div className="mb-2 p-3">
                                            <h4 className="text-warning mb-1">7</h4>
                                            <small className="text-muted">{t('pendingCheckouts')}</small>
                                            <div className="mt-1">
                                                <small className="text-primary">
                                                    <Calendar size={12} className="me-1" />
                                                    Active
                                                </small>
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row className="mt-3">
                                    <Col>
                                        <div className="d-flex justify-content-center">
                                            <button
                                                className="btn btn-primary btn-sm me-2"
                                                onClick={() => navigate('/management-analytics')}
                                            >
                                                <BarChart3 size={16} className="me-1" />
                                                View Full Analytics
                                            </button>
                                            <button
                                                className="btn btn-outline-secondary btn-sm"
                                                onClick={() => navigate('/executive-reports')}
                                            >
                                                <FileText size={16} className="me-1" />
                                                Generate Report
                                            </button>
                                        </div>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
        </Container>
    );
};

export default Dashboard;